import { FinancialInstitution } from '../types/financialInstitution';
import { 
  getFinancialInstitutionById, 
  createMockFinancialInstitution, 
  updateMockFinancialInstitution 
} from '../data/mockFinancialInstitutions';
import api from './api';

export type CreateFinancialInstitution = Omit<FinancialInstitution, 'id' | 'createdAt' | 'updatedAt'>;

const BASE_URL = '/financial-institutions';

/**
 * Récupère les données d'une institution financière par son ID.
 * Utilise d'abord les données mockées, puis fallback sur l'API.
 * @param institutionId - L'ID de l'institution financière.
 * @returns Les données de l'institution financière.
 */
export const getFinancialInstitution = async (institutionId: string): Promise<FinancialInstitution> => {
  // Essayer d'abord les données mockées
  const mockInstitution = getFinancialInstitutionById(institutionId);
  if (mockInstitution) {
    console.log(`🧪 Institution financière trouvée dans les données mockées: ${institutionId}`);
    return mockInstitution;
  }
  
  // Fallback sur l'API
  console.log(`🌐 Récupération de l'institution financière via API: ${institutionId}`);
  return api.get<FinancialInstitution>(`${BASE_URL}/${institutionId}`);
};

/**
 * Crée une nouvelle institution financière.
 * Utilise les données mockées pour les tests.
 * @param institutionData - Les données de l'institution à créer.
 * @returns La nouvelle institution financière créée.
 */
export const createFinancialInstitution = async (institutionData: CreateFinancialInstitution): Promise<FinancialInstitution> => {
  // Utiliser les données mockées pour la création
  console.log('🧪 Création d\'une institution financière dans les données mockées');
  return createMockFinancialInstitution(institutionData);
  
  // TODO: Intégrer l'API quand elle sera prête
  // return api.post<FinancialInstitution>(BASE_URL, institutionData);
};

/**
 * Met à jour les données d'une institution financière.
 * Utilise les données mockées pour les tests.
 * @param institutionId - L'ID de l'institution à mettre à jour.
 * @param updates - Les champs à mettre à jour.
 * @returns L'institution financière mise à jour.
 */
export const updateFinancialInstitution = async (institutionId: string, updates: Partial<FinancialInstitution>): Promise<FinancialInstitution> => {
  // Utiliser les données mockées pour la mise à jour
  console.log(`🧪 Mise à jour de l'institution financière dans les données mockées: ${institutionId}`);
  const updatedInstitution = updateMockFinancialInstitution(institutionId, updates);
  
  if (!updatedInstitution) {
    throw new Error(`Institution financière non trouvée: ${institutionId}`);
  }
  
  return updatedInstitution;
  
  // TODO: Intégrer l'API quand elle sera prête
  // return api.patch<FinancialInstitution>(`${BASE_URL}/${institutionId}`, updates);
};

/**
 * Supprime une institution financière.
 * @param institutionId - L'ID de l'institution à supprimer.
 */
export const deleteFinancialInstitution = async (institutionId: string): Promise<void> => {
  await api.delete(`${BASE_URL}/${institutionId}`);
};

/**
 * Uploade le logo d'une institution financière.
 * @param institutionId - L'ID de l'institution.
 * @param logoFile - Le fichier du logo à uploader.
 * @returns L'URL du logo uploadé.
 */
export const uploadInstitutionLogo = async (institutionId: string, logoFile: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('file', logoFile);
    formData.append('institutionId', institutionId);

    // Utiliser la nouvelle méthode postFormData pour les uploads
    return api.postFormData<{ url: string }>(`${BASE_URL}/logo/upload`, formData);
};
